import posthog from "posthog-js";

type Props = Record<string, unknown>;

declare global {
  interface Window {
    POSTHOG_KEY?: string;
  }
}

let initialized = false;

// Browser extensions inject content scripts that throw exceptions we can't see
// (cross-origin) or can't action (third-party code). Drop them at the SDK so they
// never reach PostHog ingestion.
const NOISE_MESSAGE_PATTERNS = [
  /^Script error\.?$/i, // cross-origin script error, browser hides details
  /Object Not Found Matching Id/i, // common Chrome extension content-script bug
  /Non-Error promise rejection captured/i, // extensions throwing string/object instead of Error
  /__firefox__/, // Firefox mobile reader-mode injection
  /window\.webkit\.messageHandlers/, // iOS in-app browser (Facebook/Instagram webview)
  /window\.ethereum/, // crypto wallet extension injection
  /ResizeObserver loop/, // benign browser-internal layout signal
  /invalid origin/, // postMessage from a browser extension/overlay we don't talk to
];

// The posthog-js SDK populates `$exception_values` (array of strings) on every
// $exception event. `$exception_message` is a legacy/fallback field that isn't
// always set, so reading only it caused the existing patterns above to never
// match. Check both, plus $exception_list[].value for completeness.
export function isExtensionNoise(event: {
  properties?: Record<string, unknown>;
}): boolean {
  const props = event.properties ?? {};
  const candidates: string[] = [];

  const exceptionMessage = props.$exception_message;
  if (typeof exceptionMessage === "string") candidates.push(exceptionMessage);

  const exceptionValues = props.$exception_values;
  if (Array.isArray(exceptionValues)) {
    for (const value of exceptionValues) {
      if (typeof value === "string") candidates.push(value);
    }
  }

  const exceptionList = props.$exception_list;
  if (Array.isArray(exceptionList)) {
    for (const item of exceptionList) {
      const value = (item as { value?: unknown })?.value;
      if (typeof value === "string") candidates.push(value);
    }
  }

  if (candidates.length === 0) return false;
  return candidates.some((m) =>
    NOISE_MESSAGE_PATTERNS.some((re) => re.test(m)),
  );
}

// In-app browsers (Facebook/Instagram/X/Reddit/Threads/etc.) often break Google
// OAuth and have lower conversion rates. Tag every event so we can segment.
const IN_APP_BROWSER_PATTERNS: Array<[RegExp, string]> = [
  [/FBAN|FBAV|FB_IAB/, "facebook"],
  [/Instagram/, "instagram"],
  [/Threads/, "threads"],
  [/Twitter|TwitterAndroid|TwitterForiPhone/, "x"],
  [/Reddit\/|RedditApp/i, "reddit"],
  [/LinkedInApp/, "linkedin"],
  [/TikTok|musical_ly/i, "tiktok"],
  [/Snapchat/, "snapchat"],
  [/Pinterest/, "pinterest"],
];

function detectInAppBrowser(): {
  is_in_app_browser: boolean;
  in_app_browser_name: string | null;
} {
  if (typeof navigator === "undefined") {
    return { is_in_app_browser: false, in_app_browser_name: null };
  }
  const ua = navigator.userAgent || "";
  for (const [pattern, name] of IN_APP_BROWSER_PATTERNS) {
    if (pattern.test(ua)) {
      return { is_in_app_browser: true, in_app_browser_name: name };
    }
  }
  return { is_in_app_browser: false, in_app_browser_name: null };
}

export function initAnalytics(): void {
  if (initialized) return;
  const key = document.querySelector<HTMLMetaElement>(
    'meta[name="posthog-key"]',
  )?.content;
  if (!key) return;

  const isAdmin =
    document.querySelector<HTMLMetaElement>('meta[name="posthog-is-admin"]')
      ?.content === "true";
  const bootstrapEl = document.querySelector<HTMLMetaElement>(
    'meta[name="posthog-bootstrap"]',
  );
  let bootstrap: { distinctID?: string; isIdentifiedID?: boolean } = {};
  if (bootstrapEl?.content) {
    try {
      bootstrap = JSON.parse(bootstrapEl.content);
    } catch {
      bootstrap = {};
    }
  }

  posthog.init(key, {
    api_host: "https://e.withkoble.com",
    ui_host: "https://us.posthog.com",
    person_profiles: "identified_only",
    capture_pageview: false,
    capture_pageleave: true,
    autocapture: true,
    capture_exceptions: true,
    capture_performance: true,
    disable_session_recording: false,
    session_recording: {
      maskAllInputs: true,
      maskInputOptions: { password: true },
      collectFonts: true,
    },
    bootstrap,
    before_send: (event) => {
      if (!event) return event;
      if (isAdmin) return null;
      if (event.event === "$exception" && isExtensionNoise(event)) return null;
      return event;
    },
    loaded: (p) => {
      if (document.documentElement.dataset.posthogOptOut === "true") {
        p.opt_out_capturing();
      }
      p.register(detectInAppBrowser());
    },
  });
  initialized = true;
}

export function captureException(error: unknown, properties?: Props): void {
  if (!initialized) return;
  posthog.captureException(error, properties);
}

export function track(event: string, properties?: Props): void {
  if (!initialized) return;
  posthog.capture(event, properties);
}

export function identifyUser(
  userId: string | number,
  properties?: Props,
): void {
  if (!initialized) return;
  posthog.identify(String(userId), properties);
}

export function resetIdentity(): void {
  if (!initialized) return;
  posthog.reset();
}

export function capturePageview(path: string): void {
  if (!initialized) return;
  posthog.capture("$pageview", { $current_url: window.location.origin + path });
}

export function optOut(): void {
  if (!initialized) return;
  posthog.opt_out_capturing();
}

export function optIn(): void {
  if (!initialized) return;
  posthog.opt_in_capturing();
}

export function hasOptedOut(): boolean {
  if (!initialized) return false;
  return posthog.has_opted_out_capturing();
}
